import { ChevronDownIcon, ChevronRightIcon } from "@chakra-ui/icons";
import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Table,
  Tbody,
  Td,
  Text,
  Tooltip,
  Textarea,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Instant } from "@js-joda/core";
import React from "react";
import { auth } from "../../core/auth";
import { ResubmitConfirmationDialog } from "../cico-reports/components/ResubmitConfirmationDialog";
import {
  getResubmitAggregatorFor,
  MAX_RESUBMIT_VISITS,
  ResubmitAggregator,
  useResubmitEvvVisits,
} from "../cico-reports/hooks/useResubmitEvvVisits";
import { VisitInstanceId } from "../../shared/schema/schema";
import useApi from "../../shared/hooks/useApi";
import DebouncedInput from "../../shared/components/DebouncedInput";
import Page from "../../shared/components/Page";
import UnauthorizedPage from "../../shared/components/UnauthorizedPage";
import { dateFormatter } from "../../shared/utils/date-formatter";
import { formatErrorResponse } from "../../shared/utils/format-response-error";
import {
  EvvDevHelpDashboardEntry,
  EvvDevHelpDashboardRequest,
  EvvDevHelpVisitStatus,
  exportEvvDevHelpRequests,
  handleEvvDevHelpVisits,
  searchEvvDevHelpRequests,
} from "./evv-dev-help.api";
import { GET_EVV_DEV_HELP_VISIT_DETAILS_QUERY } from "./evv-dev-help.graphql";
import { EvvDevHelpVisitDetail, EvvDevHelpVisitsTable } from "./components/EvvDevHelpVisitsTable";
import { useEvvDevHelpAvailability } from "./evv-dev-help.hooks";

const PAGE_SIZE = 50;
const QUERY_KEY = "evv_dev_help_dashboard";

type StatusFilter = EvvDevHelpVisitStatus | "ALL";

function formatAge(createdAt: string) {
  const hours = Math.floor((Date.now() - Instant.parse(createdAt).toEpochMilli()) / 3600000);
  if (hours < 1) return "<1h";
  if (hours < 48) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
}

function toast_error(error: unknown) {
  return {
    title: "Request failed",
    description: formatErrorResponse(error),
    status: "error" as const,
    position: "top-right" as const,
    duration: 10000,
    isClosable: true,
  };
}

export default function EvvDevHelpDashboardRoute() {
  const availability = useEvvDevHelpAvailability();

  // Fails closed: nothing while loading or on error, "unauthorized" once the server says no.
  if (availability.data === undefined) {
    return null;
  }
  if (!availability.data.canManage) {
    return <UnauthorizedPage />;
  }
  return <Dashboard />;
}

function Dashboard() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { api } = useApi();
  const handleDisclosure = useDisclosure();
  const resubmitDisclosure = useDisclosure();
  const hasResubmitPermission = auth.isAllowedTo("resubmit_evv_records");

  const [status, setStatus] = React.useState<StatusFilter>("AWAITING_DEV");
  const [officeId, setOfficeId] = React.useState("");
  const [aggregator, setAggregator] = React.useState("");
  const [errorSearch, setErrorSearch] = React.useState("");
  const [page, setPage] = React.useState(0);
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());
  const [selected, setSelected] = React.useState<Set<number>>(new Set());

  const request: EvvDevHelpDashboardRequest = {
    status,
    officeId: officeId === "" ? null : Number(officeId),
    aggregator: aggregator.trim() === "" ? null : aggregator.trim(),
    errorSearch: errorSearch.trim() === "" ? null : errorSearch.trim(),
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: [QUERY_KEY, request],
    queryFn: () => searchEvvDevHelpRequests(request),
  });

  const requests = React.useMemo(() => query.data?.requests ?? [], [query.data]);
  const offices = query.data?.offices ?? [];
  const totalRequests = query.data?.totalRequests ?? 0;
  const pageCount = Math.max(1, Math.ceil(totalRequests / PAGE_SIZE));

  // Filters change the result set, so a selection made against the old one is dropped.
  const changeFilter = (apply: () => void) => {
    apply();
    setPage(0);
    setSelected(new Set());
  };

  const allVisibleVisitIds = requests.flatMap((r) => r.visits.map((v) => v.visitInstanceId));
  // Which re-submit endpoint each visit on screen goes to, from the aggregator the dashboard
  // reports for it. Collapsed requests have no GraphQL details, so the details cannot be used.
  const resubmitGroups = React.useMemo(() => {
    const aggregatorByVisitId = new Map<number, string>();
    for (const request of requests) {
      for (const visit of request.visits) {
        aggregatorByVisitId.set(visit.visitInstanceId, visit.aggregator);
      }
    }
    const groups: Record<ResubmitAggregator, VisitInstanceId[]> = { sandata: [], hhax: [] };
    for (const id of selected) {
      const aggregator = getResubmitAggregatorFor(aggregatorByVisitId.get(id));
      if (aggregator !== null) groups[aggregator].push(id as VisitInstanceId);
    }
    return groups;
  }, [requests, selected]);
  const resubmittableCount = resubmitGroups.sandata.length + resubmitGroups.hhax.length;

  const resubmitMutation = useResubmitEvvVisits({
    onSuccess: () => {
      setSelected(new Set());
      resubmitDisclosure.onClose();
      // Prefix of both the request list and the visit-details query.
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    // Keep the selection so the developer can retry.
    onError: resubmitDisclosure.onClose,
  });

  // A visit can sit under several requests; it is listed once, in first-seen order.
  const idsToCopy = [...new Set(selected.size > 0 ? [...selected] : allVisibleVisitIds)];

  const exportMutation = useMutation({
    mutationFn: () => exportEvvDevHelpRequests({ ...request, limit: 200, offset: 0 }),
    onSuccess: (csv) => {
      const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = "evv-dev-help.csv";
      link.click();
      URL.revokeObjectURL(url);
    },
    onError: (error) => toast(toast_error(error)),
  });

  // The EVV row of every visit in an expanded request, in one call.
  const expandedVisitIds = requests
    .filter((r) => expanded.has(`${r.id}-${r.officeId}`))
    .flatMap((r) => r.visits.map((v) => v.visitInstanceId))
    .sort((a, b) => a - b);
  const detailsQuery = useQuery({
    queryKey: [QUERY_KEY, "details", expandedVisitIds],
    enabled: expandedVisitIds.length > 0,
    queryFn: () =>
      api.graphql(GET_EVV_DEV_HELP_VISIT_DETAILS_QUERY, {
        variables: {
          visitInstanceIds: expandedVisitIds as any,
          limit: expandedVisitIds.length,
        },
      }),
    placeholderData: keepPreviousData,
  });
  const detailsById = React.useMemo(() => {
    const map = new Map<number, EvvDevHelpVisitDetail>();
    for (const node of detailsQuery.data?.clockedVisitInstances.nodes ?? []) {
      map.set(Number(node.id), node);
    }
    return map;
  }, [detailsQuery.data]);

  const toggleExpanded = (id: string) =>
    setExpanded((prev) => {
      const next = new Set(prev);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  const toggleVisits = (visitIds: number[], checked: boolean) =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of visitIds) {
        if (checked) next.add(id);
        else next.delete(id);
      }
      return next;
    });

  return (
    <Page isLoading={query.isFetching}>
      <Page.Header>
        <Page.Title size="xl">EVV dev help requests ({totalRequests})</Page.Title>
      </Page.Header>
      <Page.Content>
        <Flex align="flex-end" gap={3} mb={4} wrap="wrap">
          <FormControl w="220px">
            <FormLabel fontSize="md">Status</FormLabel>
            <Select
              value={status}
              onChange={(e) => changeFilter(() => setStatus(e.target.value as StatusFilter))}
            >
              <option value="AWAITING_DEV">Awaiting dev</option>
              <option value="HANDLED">Handled</option>
              <option value="ALL">All</option>
            </Select>
          </FormControl>
          <FormControl w="220px">
            <FormLabel fontSize="md">Office</FormLabel>
            <Select
              value={officeId}
              onChange={(e) => changeFilter(() => setOfficeId(e.target.value))}
            >
              <option value="">All offices</option>
              {offices.map((office) => (
                <option key={office.id} value={office.id}>
                  {office.name}
                </option>
              ))}
            </Select>
          </FormControl>
          <FormControl w="230px">
            <FormLabel fontSize="md">Aggregator</FormLabel>
            <Input
              placeholder="e.g. Sandata"
              value={aggregator}
              onChange={(e) => changeFilter(() => setAggregator(e.target.value))}
            />
          </FormControl>
          <FormControl w="290px">
            <FormLabel fontSize="md">Error text</FormLabel>
            <DebouncedInput
              debounce={300}
              placeholder="Search errors..."
              value={errorSearch}
              onChange={(x) => changeFilter(() => setErrorSearch(String(x)))}
            />
          </FormControl>
          <Button
            isDisabled={idsToCopy.length === 0}
            variant="outline"
            onClick={() => {
              navigator.clipboard.writeText(idsToCopy.join(", "));
              toast({
                title: `Copied ${idsToCopy.length} visit IDs`,
                status: "success",
                position: "top-right",
                duration: 3000,
              });
            }}
          >
            Copy visit IDs ({idsToCopy.length})
          </Button>
          <Button
            isLoading={exportMutation.isPending}
            variant="outline"
            onClick={() => exportMutation.mutate()}
          >
            Export CSV
          </Button>
          {hasResubmitPermission && (
            <Tooltip
              isDisabled={resubmittableCount > 0 && resubmittableCount <= MAX_RESUBMIT_VISITS}
              label={
                resubmittableCount > MAX_RESUBMIT_VISITS
                  ? `Maximum ${MAX_RESUBMIT_VISITS} visits can be re-submitted at once`
                  : "No selected visit can be re-submitted"
              }
            >
              <Button
                colorScheme="blue"
                isDisabled={resubmittableCount === 0 || resubmittableCount > MAX_RESUBMIT_VISITS}
                variant="outline"
                onClick={resubmitDisclosure.onOpen}
              >
                Re-submit ({resubmittableCount})
              </Button>
            </Tooltip>
          )}
          <Button
            colorScheme="blue"
            isDisabled={selected.size === 0}
            onClick={handleDisclosure.onOpen}
          >
            Mark handled ({selected.size})
          </Button>
        </Flex>

        {query.isError && (
          <Text color="red.600" mb={3}>
            {formatErrorResponse(query.error)}
          </Text>
        )}

        {/* Fixed layout so the expanded visit table scrolls inside its own cell instead of
            stretching this one past the card. */}
        <Table size="md" sx={{ tableLayout: "fixed" }} w="100%">
          <Thead>
            <Tr>
              <Th w="40px" />
              <Th w="40px" />
              <Th w="140px">Office</Th>
              <Th>Reason</Th>
              <Th w="120px">Requested by</Th>
              <Th w="64px">Age</Th>
              <Th isNumeric w="76px">
                Visits
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {requests.map((entry) => (
              <RequestRows
                key={`${entry.id}-${entry.officeId}`}
                details={detailsById}
                entry={entry}
                isExpanded={expanded.has(`${entry.id}-${entry.officeId}`)}
                isLoadingDetails={detailsQuery.isFetching}
                selected={selected}
                onToggleExpanded={() => toggleExpanded(`${entry.id}-${entry.officeId}`)}
                onToggleVisits={toggleVisits}
              />
            ))}
            {requests.length === 0 && !query.isFetching && (
              <Tr>
                <Td colSpan={7}>
                  <Text color="gray.500" py={4} textAlign="center">
                    No requests match these filters.
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>

        <Flex align="center" gap={3} justify="flex-end" mt={4}>
          <Button isDisabled={page === 0} size="md" onClick={() => setPage(page - 1)}>
            Previous
          </Button>
          <Text fontSize="md">
            Page {page + 1} of {pageCount}
          </Text>
          <Button isDisabled={page + 1 >= pageCount} size="md" onClick={() => setPage(page + 1)}>
            Next
          </Button>
        </Flex>
      </Page.Content>

      <ResubmitConfirmationDialog
        details={
          `${resubmittableCount} of ${selected.size} selected visits can be re-submitted` +
          ` (Sandata: ${resubmitGroups.sandata.length}, HHAeXchange: ${resubmitGroups.hhax.length}).`
        }
        isLoading={resubmitMutation.isPending}
        isOpen={resubmitDisclosure.isOpen}
        visitCount={resubmittableCount}
        onClose={resubmitDisclosure.onClose}
        onConfirm={() =>
          resubmitMutation.mutate([
            { aggregator: "sandata", visitInstanceIds: resubmitGroups.sandata },
            { aggregator: "hhax", visitInstanceIds: resubmitGroups.hhax },
          ])
        }
      />

      <MarkHandledModal
        isOpen={handleDisclosure.isOpen}
        visitInstanceIds={[...selected]}
        onClose={handleDisclosure.onClose}
        onHandled={() => {
          setSelected(new Set());
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        }}
      />
    </Page>
  );
}

function RequestRows(props: {
  entry: EvvDevHelpDashboardEntry;
  isExpanded: boolean;
  selected: Set<number>;
  details: Map<number, EvvDevHelpVisitDetail>;
  isLoadingDetails: boolean;
  onToggleExpanded: () => void;
  onToggleVisits: (visitIds: number[], checked: boolean) => void;
}) {
  const { entry, selected } = props;
  const visitIds = entry.visits.map((v) => v.visitInstanceId);
  const selectedCount = visitIds.filter((id) => selected.has(id)).length;

  return (
    <>
      <Tr _hover={{ bg: "gray.50" }} cursor="pointer" onClick={props.onToggleExpanded}>
        <Td>{props.isExpanded ? <ChevronDownIcon /> : <ChevronRightIcon />}</Td>
        <Td onClick={(e) => e.stopPropagation()}>
          <Checkbox
            isChecked={visitIds.length > 0 && selectedCount === visitIds.length}
            isIndeterminate={selectedCount > 0 && selectedCount < visitIds.length}
            onChange={(e) => props.onToggleVisits(visitIds, e.target.checked)}
          />
        </Td>
        <Td>{entry.officeName}</Td>
        <Td maxW="420px">
          <Text as="span">{entry.reasonText}</Text>
        </Td>
        <Td>{entry.createdByName}</Td>
        <Td title={dateFormatter.toDateTime(entry.createdAt)}>{formatAge(entry.createdAt)}</Td>
        <Td isNumeric>{entry.visits.length}</Td>
      </Tr>
      {props.isExpanded && (
        <Tr>
          <Td bg="gray.50" colSpan={7} p={3}>
            <EvvDevHelpVisitsTable
              details={props.details}
              isLoadingDetails={props.isLoadingDetails}
              selected={selected}
              visits={entry.visits}
              onToggleVisit={(id, checked) => props.onToggleVisits([id], checked)}
            />
          </Td>
        </Tr>
      )}
    </>
  );
}

function MarkHandledModal(props: {
  isOpen: boolean;
  visitInstanceIds: number[];
  onClose: () => void;
  onHandled: () => void;
}) {
  const toast = useToast();
  const [note, setNote] = React.useState("");

  React.useEffect(() => {
    if (props.isOpen) setNote("");
  }, [props.isOpen]);

  const mutation = useMutation({
    mutationFn: () =>
      handleEvvDevHelpVisits({
        visitInstanceIds: props.visitInstanceIds,
        note: note.trim() === "" ? null : note.trim(),
      }),
    onSuccess: (result) => {
      toast({
        title: `${result.handledVisitInstanceIds.length} visit(s) marked handled`,
        status: "success",
        position: "top-right",
        duration: 5000,
      });
      props.onHandled();
      props.onClose();
    },
    onError: (error) => toast(toast_error(error)),
  });

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Mark {props.visitInstanceIds.length} visit(s) handled</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <FormControl>
            <FormLabel>Note for the agency (optional)</FormLabel>
            <Textarea rows={4} value={note} onChange={(e) => setNote(e.target.value)} />
            <FormHelperText>
              The person who asked for help gets an email with your note and the list of visits.
            </FormHelperText>
          </FormControl>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={props.onClose}>
            Cancel
          </Button>
          <Button
            colorScheme="blue"
            isLoading={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Mark handled
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
